/**
 * =============================================================================
 * CLOSE CASE - JOURNEY DIALOG
 * =============================================================================
 *
 * The same case tracking workflow used on the case details page, shown as a
 * dialog from the Referred Cases action menu. It reuses `useCaseWorkflow`,
 * `JourneyStepCard` and `JourneyStepBody` unchanged - only the surface differs,
 * so the two screens can never drift apart.
 *
 * Two phases:
 *
 *   1. acknowledgment - the case summary, the two documents and the confirm
 *      checkbox, with no step chrome around them
 *   2. the numbered rail from step 1 onwards
 * =============================================================================
 */

import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Dialog,
    Typography,
    IconButton,
    Skeleton,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "sonner";

import JourneySections, { JOURNEY_LAYOUT } from "./JourneySections";
import JourneyStepBody from "./JourneyStepBody";
import useCaseWorkflow from "./useCaseWorkflow";
import useJourneyConfirmations from "./useJourneyConfirmations";
import JourneyConfirmDialog from "./JourneyConfirmDialog";

import { NODE_TYPE } from "./journeyConfig";
import {
    getJourneyDocuments,
    openJourneyDocument,
} from "./caseDataAdapters";
import { FONT, JOURNEY_COLORS, stepCaptionSx } from "./journeyStyles";

import { useGetCaseJourney, submitJourneyStep } from "../../../api/cases";

/* -----------------------------------------------------------------------------
   CASE SUMMARY
----------------------------------------------------------------------------- */

const CaseSummary = ({ caseData }) => {
    const cells = [
        {
            label: "REF NO",
            value: caseData?.report_id ? "#" + caseData.report_id : "--",
        },
        {
            label: "NAME",
            value: caseData?.patient_name || "--",
        },
        {
            label: "PATIENT ID",
            value:
                caseData?.patient_id || caseData?.patient_user_id || "--",
        },
        {
            label: "CENTRE",
            value:
                caseData?.referred_by_center_name ||
                caseData?.referred_to_center_name ||
                "--",
        },
    ];

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(4, minmax(0, 1fr))",
                },
                gap: { xs: "12px", sm: "16px" },
                padding: { xs: "12px", sm: "16px" },
                marginBottom: { xs: "12px", sm: "16px" },
                borderRadius: "10px",
                border: "1px solid " + JOURNEY_COLORS.border,
                boxSizing: "border-box",
            }}
        >
            {cells.map((cell) => (
                <Box key={cell.label} sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            ...stepCaptionSx,
                            fontSize: "11px",
                            letterSpacing: "0.8px",
                        }}
                    >
                        {cell.label}
                    </Typography>

                    <Typography
                        title={String(cell.value)}
                        sx={{
                            fontFamily: FONT,
                            fontSize: "14px",
                            lineHeight: "20px",
                            fontWeight: 500,
                            color: JOURNEY_COLORS.text,
                            marginTop: "6px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {cell.value}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

/* -----------------------------------------------------------------------------
   WORKFLOW BODY

   Kept separate so the hook is mounted fresh every time the dialog opens and
   a previous case's answers can never leak into the next one.
----------------------------------------------------------------------------- */

const CloseCaseWorkflow = ({
    caseData,
    title,
    subtitle,
    onExit,
    onStepSave,
    onClosed,
    onConfirmOpenChange,
}) => {
    const reportId = caseData?.report_id;

    const { journey, isLoading, mutate } = useGetCaseJourney(reportId);

    const handleSubmit = useCallback(
        (body) => submitJourneyStep(reportId, body),
        [reportId]
    );

    const workflow = useCaseWorkflow({
        journey,
        isLoading,
        recordedBy:
            caseData?.referred_by_admin_name ||
            caseData?.referred_to_admin_name ||
            "",
        referredAt: caseData?.referred_on || null,
        onSubmit: handleSubmit,
        onStepSave: (entry, context) => {
            mutate();

            if (typeof onStepSave === "function") {
                onStepSave(entry, context);
            }

            /* The backend closed the case referral as part of the terminal
               step, so this is a notification, not the thing that closes it. */
            if (context.isClosed && typeof onClosed === "function") {
                onClosed(context.journey, entry);
            }
        },
        onError: (message) => toast.error(message),
    });

    const { dialogProps, submitStep, requestExit } = useJourneyConfirmations({
        workflow,
        caseData,
        onExit,
    });

    const documents = getJourneyDocuments(caseData, workflow.documents);

    const isAcknowledging =
        workflow.currentNode?.type === NODE_TYPE.ACKNOWLEDGMENT;

    /* A confirmation replaces this popup rather than stacking on top of it, so
       the surrounding dialog is told when one opens. */
    const isConfirming = Boolean(dialogProps.open);

    useEffect(() => {
        if (typeof onConfirmOpenChange === "function") {
            onConfirmOpenChange(isConfirming);
        }
    }, [isConfirming, onConfirmOpenChange]);

    return (
        <>
            {/* ============ HEADER ============ */}

            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "12px",
                    padding: { xs: "16px 16px 10px", sm: "22px 24px 14px" },
                    /* Full screen on a phone: the header stays put and only the
                       workflow below it scrolls. */
                    flexShrink: 0,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontSize: { xs: "17px", sm: "20px" },
                            lineHeight: { xs: "23px", sm: "26px" },
                            fontWeight: 700,
                            letterSpacing: "-0.01em",
                            color: JOURNEY_COLORS.text,
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontSize: { xs: "12px", sm: "13px" },
                            lineHeight: { xs: "18px", sm: "20px" },
                            fontWeight: 400,
                            color: JOURNEY_COLORS.textMuted,
                            marginTop: "2px",
                        }}
                    >
                        {subtitle}
                    </Typography>
                </Box>

                <IconButton
                    onClick={requestExit}
                    size="small"
                    aria-label="Close"
                    sx={{ color: JOURNEY_COLORS.text, marginTop: "-4px" }}
                >
                    <CloseIcon sx={{ fontSize: "22px" }} />
                </IconButton>
            </Box>

            {/* ============ WORKFLOW ============ */}

            <Box
                sx={{
                    padding: { xs: "0 14px 18px", sm: "0 24px 24px" },
                    overflowY: "auto",
                    /* minHeight lets this shrink inside the flex column Paper
                       instead of pushing the header off screen. */
                    flex: 1,
                    minHeight: 0,
                }}
            >
                {workflow.isLoading && !workflow.isReady ? (
                    <>
                        <CaseSummary caseData={caseData} />

                        <Skeleton
                            variant="rectangular"
                            height={220}
                            sx={{ borderRadius: "10px" }}
                        />
                    </>
                ) : isAcknowledging ? (
                    <>
                        <CaseSummary caseData={caseData} />

                        <JourneyStepBody
                            node={workflow.currentNode}
                            draft={workflow.draft}
                            errors={workflow.errors}
                            canSubmit={workflow.canSubmit}
                            pendingRequirement={workflow.pendingRequirement}
                            isSaving={workflow.isSubmitting}
                            showDescription={false}
                            documents={documents}
                            onAcknowledgedChange={workflow.setAcknowledged}
                            onDocumentDownload={openJourneyDocument}
                            onSubmit={submitStep}
                        />
                    </>
                ) : (
                    <JourneySections
                        workflow={workflow}
                        documents={documents}
                        isSaving={workflow.isSubmitting}
                        layout={JOURNEY_LAYOUT.RAIL}
                        hiddenNodeIds={[workflow.definition?.startNodeId]}
                        onDocumentDownload={openJourneyDocument}
                        onSubmitStep={submitStep}
                    />
                )}
            </Box>

            {/* ============ CONFIRMATIONS ============ */}

            <JourneyConfirmDialog {...dialogProps} />
        </>
    );
};

/* -----------------------------------------------------------------------------
   DIALOG
----------------------------------------------------------------------------- */

const CloseCaseJourneyDialog = ({
    open = false,
    caseData = null,
    title = "Close Case",
    subtitle = "Complete the required details before closing the case.",
    onClose,
    onStepSave,
    onClosed,
}) => {
    /*
     * While a confirmation is up, this popup steps out of the way instead of
     * sitting behind it. It stays mounted - hiding rather than closing - so the
     * workflow and the unsaved draft survive a "Go Back".
     *
     * The confirmation is a dialog of its own and portals to the body, so it is
     * a sibling of this one in the DOM and is not hidden along with it.
     */
    const [isConfirming, setIsConfirming] = useState(false);

    /*
     * A multi step form in a 460px card on a 360px screen leaves almost nothing
     * for the content, so on phones it takes the whole viewport instead.
     */
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        if (!open) {
            setIsConfirming(false);
        }
    }, [open]);

    return (
        <Dialog
            open={open}
            /* Dismissing is routed through the workflow so an in progress journey
               asks before it is abandoned. */
            onClose={(event, reason) => {
                if (reason === "backdropClick" || reason === "escapeKeyDown") {
                    return;
                }

                if (typeof onClose === "function") {
                    onClose();
                }
            }}
            fullWidth
            maxWidth="sm"
            fullScreen={isPhone}
            sx={{
                visibility: isConfirming ? "hidden" : "visible",
            }}
            PaperProps={{
                sx: {
                    borderRadius: isPhone ? 0 : "20px",
                    boxShadow: "var(--app-shadow)",
                    backgroundColor: JOURNEY_COLORS.surface,
                    maxHeight: isPhone ? "100%" : "88vh",
                },
            }}
        >
            {open && (
                <CloseCaseWorkflow
                    key={caseData?.report_id || "close-case"}
                    caseData={caseData}
                    title={title}
                    subtitle={subtitle}
                    onExit={onClose}
                    onStepSave={onStepSave}
                    onClosed={onClosed}
                    onConfirmOpenChange={setIsConfirming}
                />
            )}
        </Dialog>
    );
};

export default CloseCaseJourneyDialog;
